import StatusChip, { type RecordState } from './StatusChip'
import { CATEGORY_FILL, type DayEntry } from './scheduleDay'
import { formatScheduleLabel, useTimeFormat } from '../../lib/timeFormat'

export type SecondaryDayEntry = {
  id: string
  title: string
  detail: string
  state: RecordState
}

export default function DayCard({
  entry,
  expanded = false,
  today = false,
  flagged = false,
  leaveEntries = [],
}: {
  entry: DayEntry
  expanded?: boolean
  today?: boolean
  /** Rings the card in the warning color: a conflict the viewer should notice, distinct from `today`. */
  flagged?: boolean
  /**
   * A day can hold more than one entry, per docs/product/scheduling-model.md's
   * schedule entry record: a leave request covering this date is not a
   * replacement for its own work status, but a second entry alongside it.
   */
  leaveEntries?: SecondaryDayEntry[]
}) {
  const { format } = useTimeFormat()

  return (
    <div
      className={`rounded-surface border border-line ${CATEGORY_FILL[entry.category]} ${
        today ? 'ring-2 ring-primary ring-offset-1' : flagged ? 'ring-2 ring-warning ring-offset-1' : ''
      } ${expanded ? 'p-5' : 'p-3'}`}
    >
      <div
        className={`flex items-center gap-3 ${
          expanded ? 'flex-col items-stretch gap-4 text-center sm:flex-row sm:items-center sm:justify-center sm:text-left' : ''
        }`}
      >
        <div className={`flex-none text-center ${expanded ? 'w-full sm:w-16' : 'w-11'}`}>
          <p className="text-caption font-bold tracking-wide text-ink-subtle uppercase">{entry.weekday}</p>
          <p className={`font-extrabold ${expanded ? 'text-h1' : 'text-h4'}`}>{entry.day}</p>
        </div>
        <div className={`min-w-0 ${expanded ? 'sm:flex-none' : 'flex-1'}`}>
          <p className={`font-bold [font-variant-numeric:tabular-nums] ${expanded ? 'text-h3' : 'text-small'}`}>
            {formatScheduleLabel(entry.label, format)}
          </p>
          {entry.detail && <p className="mt-0.5 text-small text-ink-muted">{entry.detail}</p>}
          {today && !expanded && <p className="mt-0.5 text-caption font-bold text-primary">Today</p>}
        </div>
        {entry.state && (
          <span className="flex-none">
            <StatusChip state={entry.state} />
          </span>
        )}
      </div>
      {leaveEntries.map((leave) => (
        <div key={leave.id} className="mt-2 flex items-center justify-between gap-3 border-t border-line pt-2">
          <div className="min-w-0">
            <p className="text-small font-bold">{leave.title}</p>
            <p className="mt-0.5 text-small text-ink-muted">{leave.detail}</p>
          </div>
          <span className="flex-none">
            <StatusChip state={leave.state} />
          </span>
        </div>
      ))}
    </div>
  )
}
